import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { generateSimpleInvoicePDF, generateAIAInvoicePDF } from '../../utils/invoicePDF'

import { fmtMoney, fmtDate } from '../../utils/formatters'
import { ARROW_L, CHECK, PDF_ICON } from '../../utils/icons'

export default function InvoicesPage() {
  var { user, profile, activeBusiness } = useAuth()
  var isOwner = profile?.role === 'owner'
  var isOffice = profile?.role === 'office_staff'
  var canEdit = isOwner || isOffice || profile?.role === 'project_manager'

  var [tab, setTab] = useState('list')
  var [invoices, setInvoices] = useState([])
  var [loading, setLoading] = useState(true)
  var [activeInvoice, setActiveInvoice] = useState(null)
  var [invoiceLines, setInvoiceLines] = useState([])
  var [bids, setBids] = useState([])
  var [showCreateModal, setShowCreateModal] = useState(false)
  var [showSend, setShowSend] = useState(false)
  var [sendLoading, setSendLoading] = useState(false)

  // Debounce
  var pendingRef = useRef({})
  var timerRef = useRef(null)

  useEffect(function() {
    return function() { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [])

  var fetchInvoices = useCallback(async function() {
    setLoading(true)
    var q = supabase.from('invoices').select('*').order('created_at', { ascending: false })
    if (activeBusiness !== 'all') q = q.eq('business_id', activeBusiness)
    var { data } = await q
    setInvoices(data || [])

    var bq = supabase.from('bids').select('*').in('status', ['awarded', 'completed']).order('created_at', { ascending: false })
    if (activeBusiness !== 'all') bq = bq.eq('business_id', activeBusiness)
    var { data: bData } = await bq
    var bidRows = bData || []

    // A bid whose job was cancelled is not invoiceable, but the bid itself stays
    // flagged 'awarded' — cancelling from the job card never reset it. Drop those
    // so called-off work stops appearing as billable.
    // (bids.job_id has no FK to jobs, so this cannot be a PostgREST embed.)
    var jobIds = bidRows.map(function(b) { return b.job_id }).filter(Boolean)
    var cancelledJobIds = {}
    if (jobIds.length > 0) {
      var { data: jRows } = await supabase.from('jobs').select('id').eq('status', 'cancelled').in('id', jobIds)
      ;(jRows || []).forEach(function(j) { cancelledJobIds[j.id] = true })
    }
    setBids(bidRows.filter(function(b) { return !b.job_id || !cancelledJobIds[b.job_id] }))

    setLoading(false)
  }, [activeBusiness])

  useEffect(function() { fetchInvoices() }, [fetchInvoices])

  var openInvoice = async function(inv) {
    setActiveInvoice(inv)
    var { data: lines } = await supabase.from('invoice_lines').select('*').eq('invoice_id', inv.id).order('sort_order')
    setInvoiceLines(lines || [])
    setTab('detail')
  }

  var createFromBid = async function(bid, invType) {
    // Get bid lines
    var { data: bidLines } = await supabase.from('bid_lines').select('*').eq('bid_id', bid.id).order('sort_order')
    if (!bidLines || bidLines.length === 0) { alert('This bid has no line items.'); return }

    // Count existing invoices for this bid
    var { data: existing } = await supabase.from('invoices').select('id').eq('bid_id', bid.id)
    var drawNum = (existing?.length || 0) + 1

    // Create invoice
    var { data: inv, error } = await supabase.from('invoices').insert({
      bid_id: bid.id,
      business_id: bid.business_id,
      invoice_type: invType,
      draw_number: drawNum,
      client_name: bid.client_name || '',
      client_email: bid.client_email || '',
      client_phone: bid.client_phone || '',
      site_address: bid.site_address || '',
      job_name: bid.job_name || '',
      invoice_number: '',
      payment_terms: invType === 'aia' ? 'Net 30' : 'Due on Receipt',
      retainage_pct: invType === 'aia' ? 10 : 0,
      subtotal: 0,
      total: 0,
      balance_due: 0,
      status: 'draft',
      created_by: user.id
    }).select().single()

    if (error || !inv) { alert('Error creating invoice: ' + (error?.message || '')); return }

    // Get previous draw totals if AIA
    var prevAmounts = {}
    if (invType === 'aia' && drawNum > 1) {
      var { data: prevInvs } = await supabase.from('invoices').select('id').eq('bid_id', bid.id).neq('id', inv.id)
      if (prevInvs && prevInvs.length > 0) {
        var prevIds = prevInvs.map(function(p) { return p.id })
        var { data: prevLines } = await supabase.from('invoice_lines').select('*').in('invoice_id', prevIds)
        if (prevLines) {
          prevLines.forEach(function(pl) {
            var key = pl.description || ''
            if (!prevAmounts[key]) prevAmounts[key] = 0
            prevAmounts[key] += parseFloat(pl.this_amount) || 0
          })
        }
      }
    }

    // Create invoice lines from bid lines
    var clientLines = bidLines.filter(function(l) { return l.category !== 'notes' && parseFloat(l.total_cost) > 0 })
    for (var i = 0; i < clientLines.length; i++) {
      var bl = clientLines[i]
      var sv = parseFloat(bl.total_cost) || 0
      var pa = prevAmounts[bl.description] || 0
      await supabase.from('invoice_lines').insert({
        invoice_id: inv.id,
        description: bl.description,
        scheduled_value: sv.toFixed(2),
        prev_pct: sv > 0 ? ((pa / sv) * 100).toFixed(1) : '0',
        prev_amount: pa.toFixed(2),
        this_pct: '0',
        this_amount: '0',
        total_completed: pa.toFixed(2),
        balance_to_finish: (sv - pa).toFixed(2),
        sort_order: i
      })
    }

    setShowCreateModal(false)
    openInvoice(inv)
    fetchInvoices()
  }

  // Immediate save
  var updateInvoice = async function(updates) {
    if (!activeInvoice) return
    if (timerRef.current) clearTimeout(timerRef.current)
    var all = { ...pendingRef.current, ...updates }
    pendingRef.current = {}
    await supabase.from('invoices').update({ ...all, updated_at: new Date().toISOString() }).eq('id', activeInvoice.id)
    setActiveInvoice(function(prev) { return { ...prev, ...all } })
  }

  // Debounced save
  var debouncedUpdate = function(updates) {
    if (!activeInvoice) return
    setActiveInvoice(function(prev) { return { ...prev, ...updates } })
    pendingRef.current = { ...pendingRef.current, ...updates }
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async function() {
      var all = { ...pendingRef.current }
      pendingRef.current = {}
      if (Object.keys(all).length > 0) {
        await supabase.from('invoices').update({ ...all, updated_at: new Date().toISOString() }).eq('id', activeInvoice.id)
      }
    }, 800)
  }

  var goBack = function() {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      var pending = { ...pendingRef.current }
      pendingRef.current = {}
      if (Object.keys(pending).length > 0 && activeInvoice) {
        supabase.from('invoices').update({ ...pending, updated_at: new Date().toISOString() }).eq('id', activeInvoice.id)
      }
    }
    setTab('list')
    setActiveInvoice(null)
    fetchInvoices()
  }

  var updateLine = async function(id, updates) {
    await supabase.from('invoice_lines').update(updates).eq('id', id)
    var updated = invoiceLines.map(function(l) { return l.id === id ? { ...l, ...updates } : l })
    setInvoiceLines(updated)
    recalcTotals(updated)
  }

  var recalcTotals = async function(lines) {
    var ls = lines || invoiceLines
    var sub = ls.reduce(function(sum, l) { return sum + (parseFloat(l.this_amount) || 0) }, 0)
    // eslint-disable-next-line no-unused-vars
    var totalScheduled = ls.reduce(function(sum, l) { return sum + (parseFloat(l.scheduled_value) || 0) }, 0)
    var retPct = parseFloat(activeInvoice?.retainage_pct) || 0
    var totalCompleted = ls.reduce(function(sum, l) { return sum + (parseFloat(l.prev_amount) || 0) + (parseFloat(l.this_amount) || 0) }, 0)
    var retHeld = totalCompleted * (retPct / 100)
    var prevRet = parseFloat(activeInvoice?.prev_retainage_held) || 0
    var bal = sub - (retHeld - prevRet)
    await updateInvoice({
      subtotal: sub.toFixed(2),
      retainage_held: retHeld.toFixed(2),
      total: sub.toFixed(2),
      balance_due: bal.toFixed(2)
    })
  }

  var applyPercentage = function(lineId, pct) {
    var line = invoiceLines.find(function(l) { return l.id === lineId })
    if (!line) return
    var sv = parseFloat(line.scheduled_value) || 0
    var pa = parseFloat(line.prev_amount) || 0
    var thisAmt = (sv * (parseFloat(pct) / 100)) - pa
    if (thisAmt < 0) thisAmt = 0
    var tc = pa + thisAmt
    updateLine(lineId, {
      this_pct: pct,
      this_amount: thisAmt.toFixed(2),
      total_completed: tc.toFixed(2),
      balance_to_finish: (sv - tc).toFixed(2)
    })
  }

  var applyAmount = function(lineId, amount) {
    var line = invoiceLines.find(function(l) { return l.id === lineId })
    if (!line) return
    var sv = parseFloat(line.scheduled_value) || 0
    var pa = parseFloat(line.prev_amount) || 0
    var ta = parseFloat(amount) || 0
    var tc = pa + ta
    var pct = sv > 0 ? ((tc / sv) * 100) : 0
    updateLine(lineId, {
      this_pct: pct.toFixed(1),
      this_amount: ta.toFixed(2),
      total_completed: tc.toFixed(2),
      balance_to_finish: (sv - tc).toFixed(2)
    })
  }

  // Bill 100% for simple invoices
  var billFull = function() {
    invoiceLines.forEach(function(l) {
      applyPercentage(l.id, '100')
    })
  }

  var downloadPDF = function() {
    if (!activeInvoice) return
    var doc = activeInvoice.invoice_type === 'aia'
      ? generateAIAInvoicePDF(activeInvoice, invoiceLines)
      : generateSimpleInvoicePDF(activeInvoice, invoiceLines)
    var name = 'Invoice_' + (activeInvoice.invoice_number || activeInvoice.draw_number || 'draft') + '_' + (activeInvoice.client_name || '').replace(/[^a-zA-Z0-9]/g, '_') + '.pdf'
    doc.save(name)
  }

  var uploadAndSend = async function(method, contact) {
    if (!contact) { alert('Enter ' + (method === 'email' ? 'email address' : 'phone number')); return }
    setSendLoading(true)
    try {
      var doc = activeInvoice.invoice_type === 'aia'
        ? generateAIAInvoicePDF(activeInvoice, invoiceLines)
        : generateSimpleInvoicePDF(activeInvoice, invoiceLines)
      var blob = doc.output('blob')
      var fileName = 'invoice_' + activeInvoice.id + '_' + Date.now() + '.pdf'
      var { error } = await supabase.storage.from('quotes').upload(fileName, blob, { contentType: 'application/pdf' })
      if (error) { alert('PDF upload failed: ' + error.message); setSendLoading(false); return }
      var { data: urlData } = supabase.storage.from('quotes').getPublicUrl(fileName)
      var pdfUrl = urlData.publicUrl
      var bizName = activeInvoice.business_id === 'pools' ? 'CP Quality Pools' : 'Riverbend Contracting Co.'

      if (method === 'email') {
        var subj = 'Invoice from ' + bizName + (activeInvoice.invoice_number ? ' #' + activeInvoice.invoice_number : '')
        var body = 'Hello ' + (activeInvoice.client_name || '') + ',\n\nPlease find your invoice below:\n\nAmount Due: ' + fmtMoney(activeInvoice.balance_due) + '\n\nView / Download Invoice:\n' + pdfUrl + '\n\nPayment Terms: ' + (activeInvoice.payment_terms || 'Net 30') + '\n\nThank you for your business.\n\n' + bizName + '\n(810) 923-6229'
        var resp = await fetch('/api/send-email', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: contact, subject: subj, body: body, business_id: activeInvoice.business_id })
        })
        if (!resp.ok) { var err = await resp.json().catch(function() { return {} }); throw new Error(err.error || 'Email failed') }
        alert('Invoice emailed to ' + contact + '!')
      } else {
        var msg = 'Invoice from ' + bizName + '\nAmount Due: ' + fmtMoney(activeInvoice.balance_due) + '\n\nView invoice:\n' + pdfUrl + '\n\n' + bizName + ' | (810) 923-6229'
        var smsResp = await fetch('/api/send-sms', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: '+1' + contact.replace(/\D/g, ''), message: msg, business_id: activeInvoice.business_id })
        })
        if (!smsResp.ok) { var smsErr = await smsResp.json().catch(function() { return {} }); throw new Error(smsErr.error || 'SMS failed') }
        alert('Invoice sent via SMS!')
      }
      setShowSend(false)
    } catch(e) { alert('Send failed: ' + e.message) }
    setSendLoading(false)
  }

  var deleteInvoice = async function(id) {
    if (!window.confirm('Delete this invoice?')) return
    await supabase.from('invoices').delete().eq('id', id)
    setInvoices(function(prev) { return prev.filter(function(i) { return i.id !== id }) })
    if (activeInvoice?.id === id) { setActiveInvoice(null); setTab('list') }
  }

  var markPaid = async function() {
    await updateInvoice({ status: 'paid', amount_paid: (parseFloat(activeInvoice.amount_paid || 0) + parseFloat(activeInvoice.balance_due || 0)).toFixed(2), balance_due: '0.00' })
    alert('Invoice marked as paid!')
  }

  var statusColors = { draft: '#b48c3c', sent: '#4a6a8c', paid: '#4a8c6a', overdue: '#e05c5c', void: 'rgba(255,255,255,0.3)' }

  if (loading) return <div style={s.loading}>Loading...</div>

  // === INVOICE DETAIL ===
  if (tab === 'detail' && activeInvoice) {
    var isAIA = activeInvoice.invoice_type === 'aia'
    return (
      <div style={s.container}>
        <div style={s.header}>
          <div>
            <div style={s.title}>{isAIA ? 'AIA APPLICATION #' + activeInvoice.draw_number : 'INVOICE'} {activeInvoice.invoice_number ? '#' + activeInvoice.invoice_number : ''}</div>
            <div style={s.subtitle}>{activeInvoice.client_name || 'No client'} - {activeInvoice.job_name || 'No project'}</div>
          </div>
          <div style={s.headerActions}>
            <button onClick={downloadPDF} style={s.pdfBtn}>{PDF_ICON} DOWNLOAD PDF</button>
            <button onClick={function() { setShowSend(true) }} style={s.sendBtn}>SEND INVOICE</button>
            {activeInvoice.status !== 'paid' && <button onClick={markPaid} style={s.paidBtn}>{CHECK} MARK PAID</button>}
            <button onClick={goBack} style={s.backBtn}>{ARROW_L} ALL INVOICES</button>
          </div>
        </div>

        {/* Invoice Info */}
        <div style={s.infoGrid}>
          <Field label="INVOICE #" value={activeInvoice.invoice_number} onChange={function(v) { debouncedUpdate({ invoice_number: v }) }} placeholder="e.g. INV-001" />
          <Field label="CLIENT NAME" value={activeInvoice.client_name} onChange={function(v) { debouncedUpdate({ client_name: v }) }} />
          <Field label="PROJECT" value={activeInvoice.job_name} onChange={function(v) { debouncedUpdate({ job_name: v }) }} />
          <Field label="SITE ADDRESS" value={activeInvoice.site_address} onChange={function(v) { debouncedUpdate({ site_address: v }) }} />
          <Field label="CLIENT EMAIL" value={activeInvoice.client_email} onChange={function(v) { debouncedUpdate({ client_email: v }) }} />
          <Field label="CLIENT PHONE" value={activeInvoice.client_phone} onChange={function(v) { debouncedUpdate({ client_phone: v }) }} />
          <div>
            <label style={s.formLabel}>PAYMENT TERMS</label>
            <select value={activeInvoice.payment_terms || ''} onChange={function(e) { updateInvoice({ payment_terms: e.target.value }) }} style={s.formInput}>
              {['Due on Receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60', '50% Deposit'].map(function(t) { return <option key={t} value={t}>{t}</option> })}
            </select>
          </div>
          <div>
            <label style={s.formLabel}>DUE DATE</label>
            <input type="date" value={activeInvoice.due_date || ''} onChange={function(e) { updateInvoice({ due_date: e.target.value }) }} style={s.formInput} />
          </div>
          <div>
            <label style={s.formLabel}>STATUS</label>
            <select value={activeInvoice.status} onChange={function(e) { updateInvoice({ status: e.target.value }) }} style={s.formInput}>
              {['draft', 'sent', 'paid', 'overdue', 'void'].map(function(st) { return <option key={st} value={st}>{st.toUpperCase()}</option> })}
            </select>
          </div>
          {isAIA && (
            <div>
              <label style={s.formLabel}>RETAINAGE %</label>
              <input type="number" value={activeInvoice.retainage_pct || 0} onChange={function(e) { updateInvoice({ retainage_pct: e.target.value }); setTimeout(function() { recalcTotals() }, 100) }} style={s.formInput} step="0.5" min="0" max="20" />
            </div>
          )}
          {isAIA && (
            <div>
              <label style={s.formLabel}>DRAW #</label>
              <input type="number" value={activeInvoice.draw_number || 1} onChange={function(e) { updateInvoice({ draw_number: parseInt(e.target.value) || 1 }) }} style={s.formInput} min="1" />
            </div>
          )}
          <div style={{gridColumn:'1 / -1'}}>
            <Field label="NOTES" value={activeInvoice.notes} onChange={function(v) { debouncedUpdate({ notes: v }) }} placeholder="Invoice notes..." />
          </div>
        </div>

        {/* Totals Bar */}
        <div style={s.totalsBar}>
          <TotalBox label="THIS DRAW" value={fmtMoney(activeInvoice.subtotal)} />
          {isAIA && <TotalBox label={'RETAINAGE ' + (activeInvoice.retainage_pct || 0) + '%'} value={fmtMoney(activeInvoice.retainage_held)} />}
          <TotalBox label="BALANCE DUE" value={fmtMoney(activeInvoice.balance_due)} color="#b48c3c" big />
          <TotalBox label="STATUS" value={(activeInvoice.status || 'draft').toUpperCase()} color={statusColors[activeInvoice.status] || '#b48c3c'} />
        </div>

        {/* Line Items */}
        <div style={s.section}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px'}}>
            <div style={s.sectionTitle}>{isAIA ? 'SCHEDULE OF VALUES' : 'LINE ITEMS'}</div>
            {!isAIA && <button onClick={billFull} style={s.addLineBtn}>BILL 100%</button>}
          </div>

          {isAIA ? (
            <div>
              <div style={s.aiaHeader}>
                <span style={{flex:3}}>DESCRIPTION</span>
                <span style={{flex:1, textAlign:'right'}}>SCHEDULED</span>
                <span style={{flex:1, textAlign:'right'}}>PREVIOUS</span>
                <span style={{flex:1, textAlign:'right'}}>THIS PERIOD</span>
                <span style={{flex:1, textAlign:'right'}}>COMPLETED</span>
                <span style={{flex:0.5, textAlign:'right'}}>%</span>
                <span style={{flex:1, textAlign:'right'}}>BALANCE</span>
              </div>
              {invoiceLines.map(function(line) {
                return <AIALineRow key={line.id} line={line} onApplyPct={function(pct) { applyPercentage(line.id, pct) }} onApplyAmt={function(amt) { applyAmount(line.id, amt) }} />
              })}
            </div>
          ) : (
            <div>
              <div style={s.lineHeader}>
                <span style={{flex:3}}>DESCRIPTION</span>
                <span style={{flex:1, textAlign:'right'}}>AMOUNT</span>
                <span style={{flex:1, textAlign:'right'}}>THIS INVOICE</span>
              </div>
              {invoiceLines.map(function(line) {
                return <SimpleLineRow key={line.id} line={line} onApplyAmt={function(amt) { applyAmount(line.id, amt) }} />
              })}
            </div>
          )}
        </div>

        {/* Send Modal */}
        {showSend && <SendInvoiceModal invoice={activeInvoice} onSend={uploadAndSend} onClose={function() { setShowSend(false) }} onUpdate={debouncedUpdate} loading={sendLoading} />}
      </div>
    )
  }

  // === INVOICE LIST ===
  var unpaidPools = invoices.filter(function(i) { return i.business_id === 'pools' && i.status !== 'paid' && i.status !== 'void' })
  var allInvoices = invoices

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div><div style={s.title}>INVOICES</div><div style={s.subtitle}>{invoices.length} invoices</div></div>
        <div style={s.headerActions}>
          {canEdit && <button onClick={function() { setShowCreateModal(true) }} style={s.addBtn}>+ NEW INVOICE</button>}
        </div>
      </div>

      {/* Unpaid Pools Summary */}
      {(activeBusiness === 'all' || activeBusiness === 'pools') && unpaidPools.length > 0 && (
        <div style={s.unpaidSection}>
          <div style={s.sectionTitle}>UNPAID POOL JOBS</div>
          {unpaidPools.map(function(inv) {
            return (
              <div key={inv.id} style={s.unpaidRow} onClick={function() { openInvoice(inv) }}>
                <div style={{flex:1}}>
                  <div style={{color:'#fff', fontSize:'14px', fontWeight:600}}>{inv.job_name || inv.client_name || 'Untitled'}</div>
                  <div style={{color:'rgba(255,255,255,0.35)', fontSize:'11px', marginTop:'2px'}}>{inv.client_name}{inv.due_date ? ' - Due: ' + fmtDate(inv.due_date) : ''}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{color:'#e05c5c', fontWeight:700, fontSize:'16px', fontFamily:"'Barlow Condensed',sans-serif"}}>{fmtMoney(inv.balance_due)}</div>
                  <div style={{...s.statusBadge, color: statusColors[inv.status]}}>{(inv.status || '').toUpperCase()}</div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* All Invoices */}
      {allInvoices.length === 0 && <div style={s.empty}>No invoices yet. Create one from an awarded bid.</div>}
      {allInvoices.map(function(inv) {
        return (
          <div key={inv.id} style={s.bidCard} onClick={function() { openInvoice(inv) }}>
            <div style={{flex:1}}>
              <div style={s.bidClient}>
                {inv.invoice_number ? '#' + inv.invoice_number + ' - ' : ''}
                {inv.job_name || inv.client_name || 'Untitled'}
                {inv.invoice_type === 'aia' ? ' (Draw #' + inv.draw_number + ')' : ''}
              </div>
              <div style={s.bidMeta}>
                <span style={{color: inv.business_id === 'pools' ? '#b48c3c' : '#4a8c6a'}}>{inv.business_id === 'pools' ? 'CP POOLS' : 'RIVERBEND'}</span>
                {' - '}{(inv.invoice_type || 'simple').toUpperCase()}
                {inv.due_date && <span style={{color: inv.status === 'overdue' ? '#e05c5c' : 'rgba(255,255,255,0.35)'}}>{' - DUE: '}{fmtDate(inv.due_date)}</span>}
              </div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={s.bidTotal}>{fmtMoney(inv.balance_due)}</div>
              <div style={{...s.statusBadge, color: statusColors[inv.status]}}>{(inv.status || 'draft').toUpperCase()}</div>
            </div>
            {(isOwner || isOffice) && <button onClick={function(e) { e.stopPropagation(); deleteInvoice(inv.id) }} style={s.delBtn}>X</button>}
          </div>
        )
      })}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div style={s.modalOverlay} onClick={function() { setShowCreateModal(false) }}>
          <div style={{...s.modal, maxWidth:'550px'}} onClick={function(e) { e.stopPropagation() }}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>CREATE INVOICE FROM BID</div>
              <button onClick={function() { setShowCreateModal(false) }} style={s.modalClose}>X</button>
            </div>
            <div style={{fontSize:'12px', color:'rgba(255,255,255,0.35)', marginBottom:'20px'}}>Select an awarded bid to create an invoice from its line items.</div>
            {bids.length === 0 && <div style={s.empty}>No awarded bids found. Award a bid first.</div>}
            {bids.map(function(bid) {
              return (
                <div key={bid.id} style={{...s.bidCard, flexDirection:'column', gap:'8px'}}>
                  <div style={{display:'flex', justifyContent:'space-between', width:'100%'}}>
                    <div>
                      <div style={{color:'#fff', fontWeight:600}}>{bid.job_name || bid.client_name || 'Untitled'}</div>
                      <div style={{fontSize:'11px', color:'rgba(255,255,255,0.35)'}}>
                        <span style={{color: bid.business_id === 'pools' ? '#b48c3c' : '#4a8c6a'}}>{bid.business_id === 'pools' ? 'POOLS' : 'RIVERBEND'}</span>
                        {' - '}{fmtMoney(bid.total)}
                      </div>
                    </div>
                  </div>
                  <div style={{display:'flex', gap:'8px'}}>
                    <button onClick={function() { createFromBid(bid, 'simple') }} style={s.addLineBtn}>SIMPLE INVOICE</button>
                    {bid.business_id === 'riverbend' && <button onClick={function() { createFromBid(bid, 'aia') }} style={{...s.addLineBtn, borderColor:'rgba(74,106,140,0.4)', color:'#4a6a8c', backgroundColor:'rgba(74,106,140,0.15)'}}>AIA G702/G703</button>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// === AIA LINE ROW ===
function AIALineRow({ line, onApplyPct, onApplyAmt }) {
  var [editing, setEditing] = useState(false)
  var [pctVal, setPctVal] = useState(line.this_pct || '0')
  var [amtVal, setAmtVal] = useState(line.this_amount || '0')
  var sv = parseFloat(line.scheduled_value) || 0
  var pa = parseFloat(line.prev_amount) || 0
  var ta = parseFloat(line.this_amount) || 0
  var tc = pa + ta
  var pct = sv > 0 ? (tc / sv * 100) : 0
  var bal = sv - tc

  if (editing) {
    return (
      <div style={s.aiaRow}>
        <span style={{flex:3, color:'#fff', fontSize:'12px'}}>{line.description}</span>
        <span style={{flex:1, textAlign:'right', color:'rgba(255,255,255,0.4)', fontSize:'12px'}}>{fmtMoney(sv)}</span>
        <span style={{flex:1, textAlign:'right', color:'rgba(255,255,255,0.3)', fontSize:'12px'}}>{fmtMoney(pa)}</span>
        <div style={{flex:1, display:'flex', gap:'4px', justifyContent:'flex-end'}}>
          <input type="number" value={amtVal} onChange={function(e) { setAmtVal(e.target.value) }} style={{...s.lineInput, width:'80px', textAlign:'right'}} step="0.01" placeholder="$" />
        </div>
        <span style={{flex:1, textAlign:'right', fontSize:'12px', color:'rgba(255,255,255,0.4)'}}>{fmtMoney(pa + (parseFloat(amtVal) || 0))}</span>
        <div style={{flex:0.5, display:'flex', gap:'2px', justifyContent:'flex-end'}}>
          <input type="number" value={pctVal} onChange={function(e) { setPctVal(e.target.value) }} style={{...s.lineInput, width:'50px', textAlign:'right'}} step="1" min="0" max="100" placeholder="%" />
        </div>
        <span style={{flex:1, textAlign:'right', fontSize:'12px', color:'rgba(255,255,255,0.3)'}}>{fmtMoney(sv - pa - (parseFloat(amtVal) || 0))}</span>
        <div style={{width:'50px', display:'flex', gap:'4px'}}>
          <button onClick={function() { if (pctVal !== line.this_pct) { onApplyPct(pctVal) } else { onApplyAmt(amtVal) }; setEditing(false) }} style={s.saveSmBtn}>{CHECK}</button>
          <button onClick={function() { setEditing(false) }} style={s.delSmBtn}>X</button>
        </div>
      </div>
    )
  }
  return (
    <div style={s.aiaRow} onClick={function() { setEditing(true); setPctVal(sv > 0 ? (tc / sv * 100).toFixed(1) : '0'); setAmtVal(String(ta)) }}>
      <span style={{flex:3, color:'#fff', fontSize:'12px'}}>{line.description}</span>
      <span style={{flex:1, textAlign:'right', color:'rgba(255,255,255,0.4)', fontSize:'12px'}}>{fmtMoney(sv)}</span>
      <span style={{flex:1, textAlign:'right', color:'rgba(255,255,255,0.3)', fontSize:'12px'}}>{fmtMoney(pa)}</span>
      <span style={{flex:1, textAlign:'right', color: ta > 0 ? '#4a8c6a' : 'rgba(255,255,255,0.2)', fontSize:'12px', fontWeight: ta > 0 ? 600 : 400}}>{ta > 0 ? fmtMoney(ta) : '--'}</span>
      <span style={{flex:1, textAlign:'right', color:'rgba(255,255,255,0.5)', fontSize:'12px'}}>{fmtMoney(tc)}</span>
      <span style={{flex:0.5, textAlign:'right', color: pct >= 100 ? '#4a8c6a' : '#b48c3c', fontSize:'11px', fontWeight:600}}>{pct.toFixed(0)}%</span>
      <span style={{flex:1, textAlign:'right', color: bal <= 0 ? '#4a8c6a' : 'rgba(255,255,255,0.3)', fontSize:'12px'}}>{fmtMoney(bal)}</span>
    </div>
  )
}

// === SIMPLE LINE ROW ===
function SimpleLineRow({ line, onApplyAmt }) {
  var [editing, setEditing] = useState(false)
  var [amtVal, setAmtVal] = useState(line.this_amount || '0')
  var sv = parseFloat(line.scheduled_value) || 0
  var ta = parseFloat(line.this_amount) || 0

  if (editing) {
    return (
      <div style={s.lineRow}>
        <span style={{flex:3, color:'#fff', fontSize:'13px'}}>{line.description}</span>
        <span style={{flex:1, textAlign:'right', color:'rgba(255,255,255,0.4)', fontSize:'13px'}}>{fmtMoney(sv)}</span>
        <div style={{flex:1, display:'flex', gap:'4px', justifyContent:'flex-end'}}>
          <input type="number" value={amtVal} onChange={function(e) { setAmtVal(e.target.value) }} style={{...s.lineInput, width:'100px', textAlign:'right'}} step="0.01" />
          <button onClick={function() { onApplyAmt(amtVal); setEditing(false) }} style={s.saveSmBtn}>{CHECK}</button>
          <button onClick={function() { setEditing(false) }} style={s.delSmBtn}>X</button>
        </div>
      </div>
    )
  }
  return (
    <div style={s.lineRow} onClick={function() { setEditing(true); setAmtVal(String(ta || sv)) }}>
      <span style={{flex:3, color:'#fff', fontSize:'13px'}}>{line.description}</span>
      <span style={{flex:1, textAlign:'right', color:'rgba(255,255,255,0.4)', fontSize:'13px'}}>{fmtMoney(sv)}</span>
      <span style={{flex:1, textAlign:'right', color: ta > 0 ? '#4a8c6a' : 'rgba(255,255,255,0.2)', fontSize:'13px', fontWeight: ta > 0 ? 600 : 400}}>{ta > 0 ? fmtMoney(ta) : '--'}</span>
    </div>
  )
}

// === SEND INVOICE MODAL ===
function SendInvoiceModal({ invoice, onSend, onClose, onUpdate, loading }) {
  var [phone, setPhone] = useState(invoice.client_phone || '')
  var [email, setEmail] = useState(invoice.client_email || '')
  return (
    <div style={s.modalOverlay} onClick={onClose}><div style={{...s.modal, maxWidth:'450px'}} onClick={function(e) { e.stopPropagation() }}>
      <div style={s.modalHeader}><div style={s.modalTitle}>SEND INVOICE</div><button onClick={onClose} style={s.modalClose}>X</button></div>
      <div style={{marginBottom:'20px'}}>
        <label style={s.formLabel}>CLIENT EMAIL</label>
        <input value={email} onChange={function(e) { setEmail(e.target.value); onUpdate({ client_email: e.target.value }) }} style={s.formInput} placeholder="client@email.com" />
        <button onClick={function() { onSend('email', email) }} disabled={loading} style={{...s.sendActionBtn, backgroundColor: loading ? '#333' : '#4a6a8c', marginTop:'8px'}}>{loading ? 'SENDING...' : 'SEND VIA EMAIL'}</button>
      </div>
      <div style={s.quoteDivider} />
      <div style={{marginTop:'20px'}}>
        <label style={s.formLabel}>CLIENT PHONE</label>
        <input value={phone} onChange={function(e) { setPhone(e.target.value); onUpdate({ client_phone: e.target.value }) }} style={s.formInput} placeholder="(810) 555-1234" />
        <button onClick={function() { onSend('sms', phone) }} disabled={loading} style={{...s.sendActionBtn, backgroundColor: loading ? '#333' : '#4a8c6a', marginTop:'8px'}}>{loading ? 'SENDING...' : 'SEND VIA TEXT'}</button>
      </div>
    </div></div>
  )
}

function Field({ label, value, onChange, placeholder }) { return <div><label style={s.formLabel}>{label}</label><input value={value||''} onChange={function(e) { onChange(e.target.value) }} style={s.formInput} placeholder={placeholder||''} /></div> }
function TotalBox({ label, value, color, big }) { return <div style={s.totalBox}><div style={s.totalLabel}>{label}</div><div style={{...s.totalValue,...(big?{fontSize:'24px'}:{}),color:color||'#ffffff'}}>{value}</div></div> }

var s = {
  container:{fontFamily:"'Barlow',sans-serif",maxWidth:'100%'},loading:{padding:'60px',textAlign:'center',color:'rgba(255,255,255,0.3)',fontSize:'15px'},
  header:{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'28px',flexWrap:'wrap',gap:'16px'},
  title:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'28px',fontWeight:700,letterSpacing:'3px',color:'#ffffff'},
  subtitle:{fontSize:'15px',color:'rgba(255,255,255,0.4)',marginTop:'4px'},
  headerActions:{display:'flex',gap:'8px',flexWrap:'wrap'},
  addBtn:{backgroundColor:'#b48c3c',border:'none',borderRadius:'3px',padding:'8px 16px',color:'#0a0f1a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:700,letterSpacing:'2px',cursor:'pointer'},
  backBtn:{backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'3px',padding:'8px 16px',color:'rgba(255,255,255,0.5)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'},
  pdfBtn:{backgroundColor:'rgba(180,140,60,0.15)',border:'1px solid rgba(180,140,60,0.4)',borderRadius:'3px',padding:'8px 16px',color:'#b48c3c',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'},
  sendBtn:{backgroundColor:'rgba(74,140,106,0.15)',border:'1px solid rgba(74,140,106,0.4)',borderRadius:'3px',padding:'8px 16px',color:'#4a8c6a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'},
  paidBtn:{backgroundColor:'rgba(74,140,106,0.3)',border:'1px solid rgba(74,140,106,0.5)',borderRadius:'3px',padding:'8px 16px',color:'#4a8c6a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:700,letterSpacing:'1.5px',cursor:'pointer'},
  sendActionBtn:{width:'100%',padding:'12px',border:'none',borderRadius:'4px',color:'#ffffff',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'14px',fontWeight:700,letterSpacing:'2px',cursor:'pointer'},
  empty:{padding:'40px',textAlign:'center',color:'rgba(255,255,255,0.2)',fontSize:'14px'},
  section:{marginBottom:'24px'},
  sectionTitle:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'14px',fontWeight:600,letterSpacing:'2px',color:'#b48c3c',marginBottom:'12px'},
  bidCard:{display:'flex',alignItems:'center',gap:'16px',padding:'16px 20px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'4px',marginBottom:'4px',cursor:'pointer',position:'relative'},
  bidClient:{fontSize:'16px',fontWeight:600,color:'#ffffff'},bidMeta:{fontSize:'12px',color:'rgba(255,255,255,0.35)',marginTop:'4px'},
  bidTotal:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'20px',fontWeight:700,color:'#ffffff'},
  statusBadge:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'1.5px',marginTop:'4px'},
  delBtn:{position:'absolute',right:'8px',top:'8px',background:'none',border:'none',color:'rgba(255,255,255,0.2)',fontSize:'14px',cursor:'pointer'},
  infoGrid:{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px',marginBottom:'24px'},
  formLabel:{display:'block',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'2px',color:'rgba(255,255,255,0.3)',marginBottom:'6px'},
  formInput:{width:'100%',padding:'10px 12px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#ffffff',fontSize:'14px',fontFamily:"'Barlow',sans-serif",outline:'none',boxSizing:'border-box'},
  totalsBar:{display:'flex',gap:'16px',padding:'20px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'4px',marginBottom:'24px',flexWrap:'wrap'},
  totalBox:{minWidth:'90px'},totalLabel:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'9px',letterSpacing:'1.5px',color:'rgba(255,255,255,0.3)'},
  totalValue:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'18px',fontWeight:700,color:'#ffffff',marginTop:'4px'},
  lineHeader:{display:'flex',gap:'8px',padding:'8px 12px',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'9px',fontWeight:600,letterSpacing:'1.5px',color:'rgba(255,255,255,0.25)',borderBottom:'1px solid rgba(255,255,255,0.06)'},
  aiaHeader:{display:'flex',gap:'8px',padding:'8px 12px',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'8px',fontWeight:600,letterSpacing:'1px',color:'rgba(255,255,255,0.25)',borderBottom:'1px solid rgba(255,255,255,0.06)'},
  lineRow:{display:'flex',gap:'8px',alignItems:'center',padding:'10px 12px',borderBottom:'1px solid rgba(255,255,255,0.04)',cursor:'pointer'},
  aiaRow:{display:'flex',gap:'8px',alignItems:'center',padding:'8px 12px',borderBottom:'1px solid rgba(255,255,255,0.04)',cursor:'pointer'},
  lineInput:{padding:'6px 8px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#ffffff',fontSize:'12px',fontFamily:"'Barlow',sans-serif",outline:'none'},
  addLineBtn:{padding:'6px 14px',backgroundColor:'rgba(180,140,60,0.15)',border:'1px solid rgba(180,140,60,0.3)',borderRadius:'3px',color:'#b48c3c',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'},
  saveSmBtn:{padding:'4px 8px',backgroundColor:'rgba(74,140,106,0.15)',border:'1px solid rgba(74,140,106,0.3)',borderRadius:'3px',color:'#4a8c6a',fontSize:'12px',cursor:'pointer'},
  delSmBtn:{padding:'4px 8px',backgroundColor:'rgba(224,92,92,0.1)',border:'1px solid rgba(224,92,92,0.2)',borderRadius:'3px',color:'#e05c5c',fontSize:'12px',cursor:'pointer'},
  unpaidSection:{padding:'20px',backgroundColor:'rgba(224,92,92,0.05)',border:'1px solid rgba(224,92,92,0.15)',borderRadius:'4px',marginBottom:'24px'},
  unpaidRow:{display:'flex',alignItems:'center',gap:'16px',padding:'12px',borderBottom:'1px solid rgba(255,255,255,0.04)',cursor:'pointer'},
  modalOverlay:{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'24px'},
  modal:{backgroundColor:'#0f1420',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'6px',padding:'32px',width:'100%',maxWidth:'650px',maxHeight:'85vh',overflow:'auto'},
  modalHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'24px'},
  modalTitle:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'18px',fontWeight:700,letterSpacing:'2px',color:'#ffffff'},
  modalClose:{background:'none',border:'none',color:'rgba(255,255,255,0.4)',fontSize:'18px',cursor:'pointer'},
  quoteDivider:{height:'1px',backgroundColor:'rgba(255,255,255,0.08)',margin:'12px 0'},
}
